//-- Copyright (c) 2021 TMT Insights Group, LLC
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isEmpty, values } from 'lodash';
import { AxiosError, AxiosResponse } from 'axios';
import {
  Constants,
  DataTableState,
  GroupProfile,
  JobDetailResponse,
  ModuleType,
  Order,
  OrderDetail,
  OrderJobAsset,
  OrderJobTask,
  Pagination,
  PolarisApiResponse,
  UserProfile
} from '@/redux/slices/slice_types';
import { getJob } from '@/redux/slices/jobSlice';
import {
  AppliedFilters,
  ArrayElement,
  BaseTableConfiguration as BaseTableConfigurationBase,
  DataTableConfig,
  DataTableConfigValues,
  FilterSettings,
  Filters,
  Params,
  Settings,
  Sorting,
  SortingObject
} from '@/__configs/config_types';
import {
  FOCUS_CALENDAR_MONTHLY_VIEW_ITEMS_PER_DAY,
  FOCUS_CALENDAR_WEEKLY_VIEW_ITEMS_PER_DAY,
  LoadingStatuses,
  PAGE_SIZE
} from '@/constants/constants';
import axios from '@/_utils/axios';
import { debounceAsync, handleReport } from '@/_utils/utils';
import { dataTableInitialState, parseFns } from '@/redux/selectors/configSelectors';
import { NewFilterTypesMap } from '@/exports/columnCommonComponents';
import { ParamsOptions } from './baseDataConfig';
import { RootState } from '@/redux/store';

const getCurrentTableData = (dataTableState: Partial<DataTableState>) => {
  const currentTable = dataTableState.currentTable;
  if (!currentTable) return;
  return dataTableState[currentTable];
};

let abortController = new AbortController();

type BaseTableConfiguration = BaseTableConfigurationBase<unknown, unknown>;

const initialState: Partial<DataTableState> = {
  currentModule: null
};

const getDataFn = (getState: () => RootState, rejectWithValue: any) => {
  const {
    auth,
    focusCalendar: { view: focusCalendarView }
  } = getState();
  const { dataTable } = getState() as { dataTable: DataTableState };
  const { settings = {} as Settings, params = {}, baseUrl, apiBaseUrl } = getCurrentTableData(dataTable) as BaseTableConfiguration;
  const currentTable = dataTable.currentTable;
  const queryParams: { [key: string]: string | string[] | boolean } = {
    ...params
  };

  Object.keys(params).forEach((key) => {
    const paramValue = params[key as keyof Params];
    if (typeof paramValue === 'string' && Object.values(ParamsOptions).includes(paramValue as ParamsOptions)) {
      const constantKey = params[key as keyof Params] as keyof Constants;
      const paramKey = key as keyof Params;
      queryParams[paramKey] = Object.keys((getState() as any).constants[constantKey]);
    }
  });
  // the tasks datatable should use the manual task names from constants for the `name_filter` param
  // all the others should continue to use param set in config until we can update
  if (params?.name_filter && currentTable === 'tasks') {
    queryParams['name_filter'] = Object.keys((getState() as any).constants.tasks.taskTypeGroups);
  }

  if (currentTable === 'jobDetailBundleJobs') {
    const job = (getState() as any).job.data;
    if (job?.job_type === 'bundle') {
      queryParams['bundle_job_hid_filter'] = [job?.hid];
    } else {
      queryParams['bundle_job_hid_filter'] = [job?.bundle_job_hid];
      queryParams['exclude_job_hid_filter'] = [job?.hid];
    }
  }

  if (
    (currentTable === 'titles' || currentTable === 'titlesV2') &&
    params?.entity_types_filter?.length &&
    !settings.filters?.entity_types_filter?.length
  ) {
    queryParams['entity_types_filter'] = params.entity_types_filter;
  }

  const executeRequest = async (customSettings: Settings) => {
    const multiSort = dataTable[currentTable].multiSort || false;
    const { body, queryString } = getBodyAndQuerystring(customSettings, queryParams, currentTable, multiSort);
    try {
      abortController.abort();
      abortController = new AbortController();

      let result = undefined;

      if (dataTable[currentTable].tableType === 'calendar') {
        const isDailyView = focusCalendarView === 'day';
        const itemsPerDay =
          focusCalendarView === 'month'
            ? FOCUS_CALENDAR_MONTHLY_VIEW_ITEMS_PER_DAY
            : focusCalendarView === 'week'
              ? FOCUS_CALENDAR_WEEKLY_VIEW_ITEMS_PER_DAY
              : undefined;

        result = await axios
          .post(
            `${baseUrl}${isDailyView ? `${queryString}` : '?page_size=99999&page_num=0'}`,
            {
              ...body,
              order_on: undefined,
              items_per_day: itemsPerDay,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            },
            {
              signal: abortController.signal,
              ...(apiBaseUrl ? { baseURL: apiBaseUrl } : {})
            }
          )
          .then((res) => ({ data: res.data, currentTable }));
      } else {
        result = await axios
          .post(`${baseUrl}search/${queryString}`, body, {
            signal: abortController.signal,
            ...(apiBaseUrl ? { baseURL: apiBaseUrl } : {})
          })
          .then((res) => {
            return {
              data: res.data,
              currentTable
            };
          });
      }

      if (result.data.data.length === 0 && customSettings.currentPage > 1) {
        return executeRequest({
          ...customSettings,
          currentPage: 1
        });
      }

      return result;
    } catch (err: unknown) {
      return rejectWithValue((err as AxiosError)?.response?.data || err);
    }
  };

  return executeRequest({ ...settings, pageSize: settings.pageSize || auth.preferences.page_size });
};

const getDataFnDebounced = debounceAsync(getDataFn, 500);

// THUNKS
export const getData = createAsyncThunk<any, undefined, { state: RootState; rejectValue?: unknown }>(
  'dataTable/getData',
  async (_args, { getState, rejectWithValue }) => {
    const state = getState();
    const currentTableData = getCurrentTableData(state.dataTable);

    if (!currentTableData) return;

    const { debounceNextDataRequest } = currentTableData;
    if (debounceNextDataRequest) {
      return getDataFnDebounced(getState, rejectWithValue);
    } else {
      return getDataFn(getState, rejectWithValue);
    }
  }
);

export const exportReport = createAsyncThunk('dataTable/exportReport', async (id: string | undefined, { getState }) => {
  const { dataTable } = getState() as { dataTable: DataTableState };
  const currentTable = id ? 'jobs' : dataTable.currentTable;
  const { settings, params, baseUrl } = id ? dataTable.jobs : (getCurrentTableData(dataTable) as BaseTableConfiguration);
  const queryParams = {
    ...params
  };

  if (params?.name_filter && currentTable === 'tasks') {
    queryParams['name_filter'] = Object.keys((getState() as any).constants.tasks.taskTypeGroups);
  }
  const { body, queryString } = getBodyAndQuerystring(settings || {}, queryParams || {}, currentTable);

  let requestUrl: string = `${baseUrl}report/${queryString}`;

  if (currentTable === 'focusAvails') {
    requestUrl = '/focus/reports/avails/';
  }

  return axios
    .post(requestUrl, id ? { search: 'W' + id } : body, {
      responseType: 'blob'
    })
    .then((response) => {
      if (response.status !== 202) {
        handleReport(response);
      } else {
        return new Blob([response.data]).text().then((text) => JSON.parse(text));
      }
    })
    .catch((err) => console.error(err));
});

export const updateStatus = createAsyncThunk(
  'dataTable/updateStatus',
  async ({ id, status }: { id: number; status: string }, { getState, rejectWithValue }) => {
    const { dataTable } = getState() as { dataTable: DataTableState };
    const { baseUrl } = getCurrentTableData(dataTable) as BaseTableConfiguration;
    return axios
      .post(`${baseUrl}${id}/`, { status })
      .then((res) => ({ data: res.data?.data, id }))
      .catch((err) => rejectWithValue(err.response));
  }
);

export const assignUserToTask = createAsyncThunk(
  'dataTable/assignUserToTask',
  async ({ id, user, type = 'tasks' }: { id: number; user: UserProfile; type: string }, { rejectWithValue }) => {
    return axios
      .post(`/core/${type === 'assets' ? 'jobs/assets' : 'tasks'}/${id}/assign/`, {
        user_id: user.id < 0 ? 0 : user.id
      })
      .then((res) => ({
        ...res.data?.data,
        assignmentType: type
      }))
      .catch((err) => rejectWithValue(err));
  }
);

export const assignGroupToTask = createAsyncThunk(
  'dataTable/assignGroupToTask',
  async ({ id, group, type = 'tasks' }: { id: number; group: GroupProfile; type: string }) => {
    return axios
      .post(`/core/${type === 'assets' ? 'jobs/assets' : 'tasks'}/${id}/assign/group/`, {
        group_id: group.id
      })
      .then((res) => res.data?.data)
      .catch((err) => err);
  }
);

export const editOrder = createAsyncThunk('dataTable/editOrder', async ({ id, ...params }: Partial<OrderDetail>) => {
  return axios.post(`/core/orders/${id}/`, params).then((res: AxiosResponse<PolarisApiResponse<Order>>) => {
    if (typeof res.data !== 'string') {
      return res.data;
    } else throw new Error();
  });
});

export const sliceConfig = {
  name: 'dataTable',
  initialState,
  reducers: {
    setModuleState(state: DataTableState, { payload }: PayloadAction<ModuleType | null>) {
      state.currentModule = payload;
    },
    setDataTables(state: DataTableState, { payload }: any) {
      Object.keys(payload).forEach((key) => {
        state[key as keyof DataTableConfig] = payload[key as keyof DataTableConfig];
      });
    },
    applyFilter(
      state: DataTableState,
      {
        payload: { type, selections, debounceNextDataRequest = false }
      }: PayloadAction<{ type: string; selections: any; debounceNextDataRequest?: boolean }>
    ) {
      const { settings = {} } = getCurrentTableData(state) as BaseTableConfiguration;
      const currentTable = state.currentTable;
      (settings as Settings).filters[type as keyof AppliedFilters] = selections;
      (settings as Settings).currentPage = 1;
      state[currentTable].selected = [];
      if (debounceNextDataRequest) {
        state[currentTable].debounceNextDataRequest = true;
      }
      if (values((settings as Settings).filters).every(isEmpty)) {
        (state[currentTable] as BaseTableConfiguration).appliedSearchName = null;
      }
    },
    applyFilters(state: DataTableState, { payload: { filters, sorting, name } }: PayloadAction<Settings>) {
      const tableData = getCurrentTableData(state);
      const settings: Partial<Settings> = tableData && tableData.settings ? tableData.settings : {};
      const currentTable = state.currentTable;
      settings.filters = filters;
      settings.sorting = sorting || (settings as Settings).sorting;
      settings.currentPage = 1;
      state[currentTable].appliedSearchName = name;
      state[currentTable].selected = [];
    },
    expandRow(state: DataTableState, { payload: { rowId } }: PayloadAction<{ rowId: number }>) {
      const currentTable = state.currentTable;
      const expandedRowIdsSet = new Set(state[currentTable]?.expandedRowIds || []);
      expandedRowIdsSet.add(rowId);
      (state[currentTable] as BaseTableConfiguration).expandedRowIds = Array.from(expandedRowIdsSet);
    },
    collapseRow(state: DataTableState, { payload: { rowId } }: PayloadAction<{ rowId: number }>) {
      const currentTable = state.currentTable;
      const expandedRowIdsSet = new Set(state[currentTable]?.expandedRowIds || []);
      expandedRowIdsSet.delete(rowId);
      (state[currentTable] as BaseTableConfiguration).expandedRowIds = Array.from(expandedRowIdsSet);
    },
    collapseAllRows(state: DataTableState, { payload: { tableKey } }: PayloadAction<{ tableKey: DataTableState['currentTable'] }>) {
      (state[tableKey] as BaseTableConfiguration).expandedRowIds = [];
    },
    setSorting(state: DataTableState, { payload }: PayloadAction<Sorting>) {
      const { settings } = getCurrentTableData(state) as BaseTableConfiguration;
      const multiSort = (getCurrentTableData(state) as BaseTableConfiguration).multiSort || false;
      if (multiSort) {
        (settings as Settings).sorting = convertSorting(payload);
      } else {
        (settings as Settings).sorting = payload;
      }
      (settings as Settings).currentPage = 1;
      state[state.currentTable].selected = [];
    },
    setParams(state: DataTableState, { payload }: PayloadAction<Params>) {
      const currentTable = state.currentTable;
      (state[currentTable] as BaseTableConfiguration).params = payload;
    },
    clearFilters(state: DataTableState) {
      const tableData = getCurrentTableData(state);
      const settings: Partial<Settings> = tableData && tableData.settings ? tableData.settings : {};
      const currentTable = state.currentTable;
      if (state[currentTable].appliedSearchName) {
        state[currentTable].appliedSearchName = null;
      }
      if (settings.filters?.hierarchy_filter) {
        settings.filters = { hierarchy_filter: settings.filters.hierarchy_filter } as FilterSettings;
      } else {
        settings.filters = {} as FilterSettings;
      }
      state[currentTable].selected = [];
      (settings as Settings).currentPage = 1;
    },
    setCurrentPage(state: DataTableState, { payload }: PayloadAction<number>) {
      const { settings } = getCurrentTableData(state) as BaseTableConfiguration;
      (settings as Settings).currentPage = payload;
    },
    setPageSize(state: DataTableState, { payload }: PayloadAction<number>) {
      const { settings } = getCurrentTableData(state) as BaseTableConfiguration;
      (settings as Settings).pageSize = payload;
    },
    setAppliedSearch(state: DataTableState, { payload }: PayloadAction<string>) {
      const { settings } = getCurrentTableData(state) as BaseTableConfiguration;
      const currentTable = state.currentTable;
      (state[currentTable] as BaseTableConfiguration).appliedSearchName = payload;
      (settings as Settings).currentPage = 1;
    },
    setCurrentTable(state: DataTableState, { payload }: PayloadAction<keyof DataTableConfig>) {
      state.currentTable = payload;
    },
    setEditedRowId(state: DataTableState, { payload: { id } }: PayloadAction<{ id?: number }>) {
      const currentTable = state.currentTable;
      const currentRowEdited = (state[currentTable] as BaseTableConfiguration).rowEditedId;
      if (id === currentRowEdited) {
        (state[currentTable] as BaseTableConfiguration).rowEditedId = {};
      } else {
        (state[currentTable] as BaseTableConfiguration).rowEditedId = id;
      }
    },
    setItemEditedValue(state: DataTableState, { payload: { key, value } }: PayloadAction<{ key: number; value: any }>) {
      const { rowEditedId } = getCurrentTableData(state) as BaseTableConfiguration;
      if (!rowEditedId || isEmpty(rowEditedId)) return;
      rowEditedId[key] = value;
    },
    selectRowItem(state: DataTableState, { payload }: PayloadAction<{ selected: ArrayElement<DataTableConfigValues['data']>; checked: boolean }>) {
      const { selected, checked } = payload;
      if (checked) {
        state[state.currentTable].selected?.push(selected);
        return;
      }

      const index = state[state.currentTable].selected?.findIndex((record) => record.id === selected?.id);
      if (index !== undefined && index > -1) {
        state[state.currentTable].selected?.splice(index, 1);
      }
    },
    selectRowHeader(state: DataTableState, { payload }: PayloadAction<DataTableConfigValues['data']>) {
      state[state.currentTable as keyof DataTableState].selected = [...payload];
    },
    setBulkErrors(state: DataTableState, { payload }: PayloadAction<Array<string[]>>) {
      const currentTable = state.currentTable;
      (state[currentTable] as BaseTableConfiguration).bulkErrors = payload;
    }
  },
  extraReducers: (builder: any) => {
    builder.addCase(getData.pending, (state: DataTableState) => {
      const currentTable = state.currentTable;
      (state[currentTable] as BaseTableConfiguration).status = LoadingStatuses.pending;
    });
    builder.addCase(getData.fulfilled, (state: DataTableState, { payload }: PayloadAction<{ currentTable: keyof DataTableConfig; data: any }>) => {
      const { currentTable, data } = payload;
      const { parseDataFn } = parseFns[currentTable] || {};
      const parsedData = parseDataFn?.(data) || data?.data || [];
      (state[currentTable] as DataTableConfigValues).data = parsedData;

      if (state[currentTable].selected?.length) {
        const selectedIds = new Set(state[currentTable].selected.map((item) => item.id));
        state[currentTable].selected = parsedData.filter((item) => selectedIds.has(item.id));
      }

      (state[currentTable] as BaseTableConfiguration).pagination = data.pagination || {};
      if (data?.pagination?.page !== undefined) {
        // Some end-points (like `POST /api/v1/focus/scheduling/summary/`) don't support pagination and don't return the
        // ` pagination` object in response.
        (state[currentTable] as BaseTableConfiguration).settings!.currentPage = data?.pagination?.page + 1;
      }
      if (state.currentTable) {
        (state[currentTable] as BaseTableConfiguration).status = LoadingStatuses.fulfilled;
      }
      state[currentTable].debounceNextDataRequest = false;
    });
    builder.addCase(getData.rejected, (state: DataTableState, { payload }) => {
      const { currentTable } = state;
      if (payload?.code !== 'ERR_CANCELED') {
        const currentTable = state.currentTable;
        state[currentTable].status = LoadingStatuses.rejected;
      }
      state[currentTable].debounceNextDataRequest = false;
    });
    builder.addCase(assignUserToTask.fulfilled, (state: DataTableState, { payload }: PayloadAction<OrderJobTask>) => {
      const currentTable = state.currentTable ?? (payload as unknown as { assignmentType: string }).assignmentType;
      const { id, assigned_user } = payload as OrderJobTask;
      (state[currentTable] as DataTableConfigValues).data = (state[currentTable] as DataTableState['tasks']).data.map((task: OrderJobTask) =>
        task.id === id ? { ...task, assigned_user } : task
      );
    });
    builder.addCase(updateStatus.pending, (state: DataTableState) => {
      const currentTable = state.currentTable;
      (state[currentTable] as BaseTableConfiguration).status = LoadingStatuses.pending;
    });
    builder.addCase(updateStatus.rejected, (state: DataTableState) => {
      const currentTable = state.currentTable;
      (state[currentTable] as BaseTableConfiguration).status = LoadingStatuses.fulfilled;
    });
    builder.addCase(updateStatus.fulfilled, (state: DataTableState, { payload }: PayloadAction<void | { data: OrderJobAsset }>) => {
      const { data } = payload as { data: OrderJobAsset };
      const currentTable = state.currentTable;
      (state[currentTable] as BaseTableConfiguration).status = LoadingStatuses.fulfilled;
      (state[currentTable] as DataTableConfigValues).data = (state[currentTable] as DataTableState['pendingAssets']).data.map(
        (task: OrderJobAsset) => (task.id === data.id ? { ...task, ...data } : task)
      );
    });

    builder.addCase(editOrder.fulfilled, (state: DataTableState, { payload }: PayloadAction<void | { data: Order }>) => {
      const { data } = payload as { data: Order };
      const currentTable = state.currentTable;
      (state[currentTable] as DataTableConfigValues).data = (state[currentTable] as DataTableState['orders']).data.map((order: Order) =>
        order.id === data.id ? { ...order, ...data, jobs: data.jobs.sort((a, b) => a.id - b.id) } : order
      );
    });

    // special case where we are loading task data into the table from the job details call
    // we set `disableGetData` on the dataConfig for jobDetailTasks to prevent the getData action
    builder.addCase(getJob.fulfilled, (state: DataTableState, { payload }: PayloadAction<JobDetailResponse>) => {
      const { data, constants } = payload;
      const stepsToDisplay = constants?.tasks.manualTaskNames ? Object.keys(constants.tasks.manualTaskNames) : [];
      let tasks: OrderJobTask[] = [];
      if (
        state?.jobDetailTasks?.assetConfirmationFilterEnabled !== true ||
        data?.additional_info?.properties?.asset_confirmation?.default !== false
      ) {
        tasks = data.tasks.filter((task: OrderJobTask) => stepsToDisplay.includes(task.name));
      }
      state.jobDetailTasks.data = tasks;
      state.jobDetailTasks.pagination = { total: tasks.length } as Pagination;
    });
  }
};

const dataTableSlice = createSlice(sliceConfig);

export const {
  setDataTables,
  applyFilter,
  applyFilters,
  clearFilters,
  setSorting,
  setParams,
  setCurrentPage,
  setPageSize,
  setAppliedSearch,
  setCurrentTable,
  setEditedRowId,
  setItemEditedValue,
  expandRow,
  collapseRow,
  collapseAllRows,
  selectRowItem,
  selectRowHeader,
  setBulkErrors,
  setModuleState
} = dataTableSlice.actions;

export default dataTableSlice.reducer;

type Body = Params & {
  [key in keyof Filters]: any;
} & {
  order_on?: string;
  order_by?: string[] | string;
  direction?: 'asc' | 'desc';
  thumbnails?: boolean;
};

const getBodyAndQuerystring = (
  settings: Partial<Settings>,
  params: Partial<Params>,
  currentTable: DataTableState['currentTable'],
  multiSort: boolean = false
) => {
  const { filters, sorting, currentPage = 1, pageSize = PAGE_SIZE } = settings;
  const filterSettings = dataTableInitialState[currentTable];
  const allFilters = filterSettings?.filters || [];
  const validFilters = allFilters.filter((filter) => filter !== undefined);
  const page = currentPage > 0 ? currentPage - 1 : 0;
  let queryString = '';
  queryString = `?page_size=${pageSize}&page_num=${page}`;
  let body: Body = params;

  if (!multiSort && !Array.isArray(sorting)) {
    const orderOnId = (sorting as SortingObject)?.id || 'external_system_id';
    body = {
      ...{
        order_on: orderOnId,
        direction: (sorting as SortingObject)?.order || 'desc'
      },
      ...body
    };
  } else {
    const order_by = convertSorting(sorting);
    body = {
      order_by,
      ...body
    };
  }

  try {
    validFilters.forEach((filter) => {
      const initSelectedValue = (filters as AppliedFilters)[filter.id];
      const filterId: string = initSelectedValue?.isContain ? filter.id.replace('_filter', '_contains') : filter.id;
      const selectedValue: any = initSelectedValue?.isContain ? initSelectedValue.values : initSelectedValue;

      if (filter.type === NewFilterTypesMap.ID && selectedValue?.length > 1) {
        body[selectedValue[1]] = typeof selectedValue[0] === 'string' ? [selectedValue[0]] : selectedValue[0];
      } else if (filter.type === NewFilterTypesMap.NULLABLE_BOOLEAN && selectedValue !== null) {
        body[filterId as keyof Body] = selectedValue;
      } else if (['availed_av_language_filter', 'subs_filter', 'dubs_filter', 'territory_filter'].indexOf(filter.id) !== -1 && selectedValue) {
        body[filterId as keyof Body] = selectedValue?.map((value: string) => value.replace('_', '-'));
      } else if (filter.id === 'title_alt_client_id_filter' && selectedValue?.length) {
        body[filterId as keyof Body] = selectedValue.split(',');
      } else if (typeof selectedValue === 'boolean' || selectedValue instanceof Array || selectedValue) {
        // const key = filter.id;
        if (filter.id === 'assigned_users_filter' || filter.id === 'assigned_groups_filter' || filter.id === 'managed_by_filter') {
          body[filterId] = selectedValue.map((user: UserProfile) => Number(user.id));
        } else if (filter.id && params[filter.id as keyof Params] && !selectedValue?.length) {
          // the tasks datatable should use the manual task names from constants for the `name_filter` param
          // all the others should continue to use param set in config until we can update
          if (!(filter.id === 'name_filter' && currentTable === 'tasks') && filter.id !== 'entity_types_filter') {
            delete body[filterId as keyof Body];
          }
        } else if (!Array.isArray(selectedValue) && selectedValue instanceof Object) {
          Object.keys(selectedValue).forEach((key) => {
            body[key as keyof Body] = selectedValue[key];
          });
        } else {
          body[filterId as keyof Body] = (filters as AppliedFilters)[filterId as keyof typeof filters];
        }
      }
    });
  } catch (err) {
    console.error('Error in getBodyAndQuerystring:', err);
  }

  return { body, queryString };
};

const convertSorting = (sorting?: Sorting) => {
  const sortingConverted = (sorting as SortingObject)?.id
    ? [((sorting as SortingObject).order === 'desc' ? '-' : '') + (sorting as SortingObject).id]
    : sorting;
  return (sortingConverted as string[]) || ['external_system_id'];
};
